import { Box, Stack } from '@mui/material'
import React from 'react'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import ErrorIcon from '@mui/icons-material/Error'
import {
  BetmenList,
  BetmenListHeader,
  BetmenListHeaderCell,
  BetmenListBody,
  BetmenListItemCard,
  BetmenListItemCardCell,
} from '../../../../../common/components'
import MedicationIcon from '@mui/icons-material/Medication'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { UsePatientEProfileReturn } from '../../hooks/usePatientEProfile'
import { Severity } from '../../../../../models/graphqlTypes'
import { PatientDetailsSectionHeader } from './PatientDetailsSectionHeader'
import { PatientDetailsActions } from './PatientDetailsActions'

export const PatientMedication: React.FC<UsePatientEProfileReturn> = ({
  patientDetails,
  isLoading,
  isEditable,
}) => {
  const { t } = useTranslation()
  const hasData = patientDetails.medications.length > 0

  return (
    <Box bgcolor={'purple.100'} py={2} borderRadius={3}>
      <PatientDetailsSectionHeader
        icon={<MedicationIcon fontSize="large" />}
        title={t('doctor:patientProfile.medication.title')}
        hasData={hasData}
      />

      {(isLoading || hasData) && (
        <BetmenList
          dataSource={patientDetails.warnings}
          loading={isLoading}
          gridColumnSizes={[4, 1, 1, 2, 2, 1, 1]}
          extraHeaderStyle={{ backgroundColor: 'transparent' }}
          stickyHeader={false}
          header={
            <BetmenListHeader>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.medication.header.name')}
              </BetmenListHeaderCell>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.medication.header.dosage')}
              </BetmenListHeaderCell>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.medication.header.severity')}
              </BetmenListHeaderCell>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.medication.header.doctor')}
              </BetmenListHeaderCell>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.medication.header.date')}
              </BetmenListHeaderCell>
              <BetmenListHeaderCell>
                {t('doctor:patientProfile.medication.header.iaTakenNow')}
              </BetmenListHeaderCell>
            </BetmenListHeader>
          }
        >
          <BetmenListBody>
            {patientDetails.medications.map((medication) => (
              <BetmenListItemCard key={medication.id}>
                <BetmenListItemCardCell typographyProps={{ fontWeight: 700 }}>
                  {medication.name}
                </BetmenListItemCardCell>
                <BetmenListItemCardCell typographyProps={{ fontWeight: 700 }}>
                  {medication.dosage}
                </BetmenListItemCardCell>
                <BetmenListItemCardCell>
                  {medication.severity && (
                    <Stack direction="row" alignItems="center" gap={1}>
                      {t(
                        `doctor:patientProfile.medication.severity.${medication.severity.toLowerCase()}`
                      )}
                      {medication.severity === Severity.High && (
                        <ErrorIcon fontSize="large" color="warning" />
                      )}
                    </Stack>
                  )}
                </BetmenListItemCardCell>
                <BetmenListItemCardCell typographyProps={{ fontWeight: 400 }}>
                  {medication.doctor}
                </BetmenListItemCardCell>
                <BetmenListItemCardCell typographyProps={{ fontWeight: 400 }}>
                  {t('common:intlDateFormattedLongMonth', {
                    date: medication.date,
                  })}
                </BetmenListItemCardCell>
                <BetmenListItemCardCell>
                  <Box
                    color={
                      medication.isTakenNow ? 'success.dark' : 'error.dark'
                    }
                    display="flex"
                    alignItems="center"
                  >
                    {medication.isTakenNow ? (
                      <CheckCircleIcon fontSize="large" />
                    ) : (
                      <HighlightOffIcon fontSize="large" />
                    )}
                  </Box>
                </BetmenListItemCardCell>
                <PatientDetailsActions isEditable={isEditable} />
              </BetmenListItemCard>
            ))}
          </BetmenListBody>
        </BetmenList>
      )}
    </Box>
  )
}
